import type {
  CreateProject,
  CreateProjectFromTemplate,
  ExportTemplate,
  Page,
  Project,
  ProjectTemplate,
  RenameProjectKey,
  UpdateProject,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/projects');

/** `GET /admin/v1/projects?includeArchived=`; items carry `canAdmin`. */
export function useProjects(options: { includeArchived?: boolean; pageSize?: number } = {}) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const includeArchived = options.includeArchived ?? false;
  return usePagedList<Project>({
    queryKey: keys.projects.list({ includeArchived, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<Project>>('GET', base, {
        query: { includeArchived, ...pageQuery(cursor, pageSize) },
        signal,
      }),
  });
}

/** `GET /admin/v1/projects/{id}`; `configVersion` feeds `If-Match` on updates. */
export function useProject(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.projects.detail(projectId ?? ''),
    queryFn: ({ signal }) => request<Project>('GET', `${base}/${projectId}`, { signal }),
    enabled: projectId !== undefined,
  });
}

export function useCreateProject() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateProject) => request<Project>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.projects.all),
  });
}

/** `POST /admin/v1/projects/from-template` */
export function useCreateProjectFromTemplate() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateProjectFromTemplate) =>
      request<Project>('POST', `${base}/from-template`, { body: input }),
    onSuccess: () => invalidate(keys.projects.all),
  });
}

/** `PATCH /admin/v1/projects/{id}` with `If-Match: <configVersion>` (409 on mismatch). */
export function useUpdateProject() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch, ifMatch }: { id: string; patch: UpdateProject; ifMatch: number }) =>
      request<Project>('PATCH', `${base}/${id}`, { body: patch, ifMatch }),
    onSuccess: (_p, { id }) => invalidate(keys.projects.all, keys.project(id).scope),
  });
}

/** `POST /admin/v1/projects/{id}/key` (rename; old keys keep resolving). */
export function useRenameProjectKey() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: RenameProjectKey & { id: string }) =>
      request<Project>('POST', `${base}/${id}/key`, { body: input }),
    onSuccess: (_p, { id }) =>
      invalidate(keys.projects.all, keys.project(id).scope, keys.tickets.all),
  });
}

/** `POST /admin/v1/projects/{id}/archive` / `unarchive` */
export function useSetProjectArchived() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      request<Project>('POST', `${base}/${id}/${archived ? 'archive' : 'unarchive'}`, {
        body: {},
      }),
    onSuccess: (_p, { id }) => invalidate(keys.projects.all, keys.projects.detail(id)),
  });
}

/** `POST /admin/v1/projects/{id}/export-template` → saved template. */
export function useExportProjectTemplate() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: ExportTemplate & { id: string }) =>
      request<ProjectTemplate>('POST', `${base}/${id}/export-template`, { body: input }),
    onSuccess: () => invalidate(keys.templates.all),
  });
}
